import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  CircularProgress,
  Alert,
  useTheme,
  useMediaQuery,
  Divider,
  IconButton,
  Grid,
} from '@mui/material';
import {
  Receipt as ReceiptIcon,
  Description as EstimateIcon,
  Payment as PaymentIcon,
  Download as DownloadIcon,
  AttachMoney as MoneyIcon,
  OpenInNew as OpenInNewIcon,
} from '@mui/icons-material';
import { BillingDetailView } from './BillingDetailView';

interface PortalBillingProps {
  customerId: string;
  tenantId: string;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`billing-tabpanel-${index}`}
      aria-labelledby={`billing-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

export const PortalBilling: React.FC<PortalBillingProps> = ({ customerId, tenantId }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(true);
  const [estimates, setEstimates] = useState<any[]>([]);
  const [invoices, setInvoices] = useState<any[]>([]);
  const [payments, setPayments] = useState<any[]>([]);
  const [detailView, setDetailView] = useState<{ open: boolean; type: 'estimate' | 'invoice' | 'payment'; data: any }>({
    open: false,
    type: 'estimate',
    data: null,
  });

  useEffect(() => {
    fetchBillingData();
  }, [customerId]);

  const fetchBillingData = async () => {
    setLoading(true);
    try {
      const API_URL = import.meta.env.VITE_SIMPLE_REST_URL || 'http://localhost:3000';
      const token = sessionStorage.getItem('portalToken');
      
      // Fetch estimates
      try {
        const estimatesResponse = await fetch(`${API_URL}/portal/estimates`, {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });
        
        if (estimatesResponse.ok) {
          const estimatesData = await estimatesResponse.json();
          setEstimates(estimatesData);
        } else {
          console.error('Failed to fetch estimates');
          setEstimates([]);
        }
      } catch (error) {
        console.error('Error fetching estimates:', error);
        setEstimates([]);
      }
      
      // Fetch invoices
      try {
        const invoicesResponse = await fetch(`${API_URL}/portal/invoices`, {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });
        
        if (invoicesResponse.ok) {
          const invoicesData = await invoicesResponse.json();
          setInvoices(invoicesData);
        } else {
          console.error('Failed to fetch invoices');
          setInvoices([]);
        }
      } catch (error) {
        console.error('Error fetching invoices:', error);
        setInvoices([]);
      }
      
      // Fetch payments
      try {
        const paymentsResponse = await fetch(`${API_URL}/portal/payments`, {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });
        
        if (paymentsResponse.ok) {
          const paymentsData = await paymentsResponse.json();
          setPayments(paymentsData);
        } else {
          console.error('Failed to fetch payments');
          setPayments([]);
        }
      } catch (error) {
        console.error('Error fetching payments:', error);
        setPayments([]);
      }
      
    } catch (error) {
      console.error('Error fetching billing data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const openDetailView = (type: 'estimate' | 'invoice' | 'payment', data: any) => {
    setDetailView({ open: true, type, data });
  };

  const closeDetailView = () => {
    setDetailView({ open: false, type: 'estimate', data: null });
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const getStatusChip = (status: string) => {
    const statusConfig: any = {
      'Paid': { color: 'success', variant: 'filled' },
      'Pending': { color: 'warning', variant: 'outlined' },
      'Overdue': { color: 'error', variant: 'filled' },
      'Sent': { color: 'info', variant: 'outlined' },
      'Draft': { color: 'default', variant: 'outlined' },
      'Completed': { color: 'success', variant: 'filled' },
    };
    
    const config = statusConfig[status] || { color: 'default', variant: 'outlined' };
    
    return (
      <Chip
        label={status}
        color={config.color}
        variant={config.variant}
        size="small"
      />
    );
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <Card>
        <CardContent sx={{ p: isMobile ? 2 : 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <MoneyIcon color="primary" sx={{ mr: 1, fontSize: isMobile ? 20 : 24 }} />
            <Typography variant={isMobile ? 'subtitle1' : 'h6'} color="primary" sx={{ fontWeight: 600 }}>
              Billing & Payments
            </Typography>
          </Box>
          <Divider sx={{ mb: 2 }} />
          
          {/* Summary Box at the top */}
          <Box sx={{ mb: 3, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <Typography variant="caption" color="text.secondary">
                  Outstanding Balance
                </Typography>
                <Typography variant="h6" color="error">
                  {formatCurrency(
                    invoices
                      .filter(inv => inv.status !== 'Paid' && inv.status !== 'Cancelled')
                      .reduce((sum, inv) => sum + (inv.amount - (inv.paid || 0)), 0)
                  )}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Typography variant="caption" color="text.secondary">
                  Total Paid (YTD)
                </Typography>
                <Typography variant="h6" color="success.main">
                  {formatCurrency(
                    payments
                      .filter(p => {
                        const paymentDate = new Date(p.date);
                        const currentYear = new Date().getFullYear();
                        return paymentDate.getFullYear() === currentYear;
                      })
                      .reduce((sum, p) => sum + p.amount, 0)
                  )}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Typography variant="caption" color="text.secondary">
                  Next Payment Due
                </Typography>
                <Typography variant="h6">
                  {invoices
                    .filter(inv => inv.dueDate && inv.status !== 'Paid' && inv.status !== 'Cancelled')
                    .sort((a, b) => new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime())
                    .slice(0, 1)
                    .map(inv => formatDate(inv.dueDate))[0] || 'No pending payments'}
                </Typography>
              </Grid>
            </Grid>
          </Box>
          
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs 
            value={tabValue} 
            onChange={handleTabChange} 
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
          >
            <Tab 
              label={isMobile ? "Estimates" : "Estimates"} 
              icon={<EstimateIcon />} 
              iconPosition={isMobile ? "top" : "start"}
              sx={{ minHeight: 48, minWidth: isMobile ? 0 : 120 }}
            />
            <Tab 
              label={isMobile ? "Invoices" : "Invoices"} 
              icon={<ReceiptIcon />} 
              iconPosition={isMobile ? "top" : "start"}
              sx={{ minHeight: 48, minWidth: isMobile ? 0 : 120 }}
            />
            <Tab 
              label={isMobile ? "Payments" : "Payments"} 
              icon={<PaymentIcon />} 
              iconPosition={isMobile ? "top" : "start"}
              sx={{ minHeight: 48, minWidth: isMobile ? 0 : 120 }}
            />
          </Tabs>
        </Box>

        {/* Estimates Tab */}
        <TabPanel value={tabValue} index={0}>
          {estimates.length === 0 ? (
            <Alert severity="info">No estimates available</Alert>
          ) : (
            <TableContainer component={Paper} variant="outlined" sx={{ overflowX: 'auto' }}>
              <Table size={isMobile ? 'small' : 'medium'} sx={{ minWidth: isMobile ? 300 : 500 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>Estimate #</TableCell>
                    {!isMobile && <TableCell>Date</TableCell>}
                    <TableCell>Amount</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {estimates.map((estimate) => (
                    <React.Fragment key={estimate.id}>
                      <TableRow hover>
                        <TableCell>
                          <Typography variant="body2" fontWeight={500}>
                            {estimate.number}
                          </Typography>
                          {isMobile && (
                            <Typography variant="caption" color="text.secondary">
                              {formatDate(estimate.date)}
                            </Typography>
                          )}
                        </TableCell>
                        {!isMobile && <TableCell>{formatDate(estimate.date)}</TableCell>}
                        <TableCell>
                          <Typography variant="body2" fontWeight={500}>
                            {formatCurrency(estimate.amount)}
                          </Typography>
                        </TableCell>
                        <TableCell>{getStatusChip(estimate.status)}</TableCell>
                        <TableCell align="center">
                          <IconButton
                            size="small"
                            onClick={() => openDetailView('estimate', estimate)}
                            title="View Details"
                          >
                            <OpenInNewIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </TabPanel>

        {/* Invoices Tab */}
        <TabPanel value={tabValue} index={1}>
          {invoices.length === 0 ? (
            <Alert severity="info">No invoices available</Alert>
          ) : (
            <TableContainer component={Paper} variant="outlined" sx={{ overflowX: 'auto' }}>
              <Table size={isMobile ? 'small' : 'medium'} sx={{ minWidth: isMobile ? 300 : 500 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>Invoice #</TableCell>
                    {!isMobile && <TableCell>Date</TableCell>}
                    <TableCell>Amount</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {invoices.map((invoice) => (
                    <TableRow key={invoice.id} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight={500}>
                          {invoice.number}
                        </Typography>
                        {isMobile && (
                          <Typography variant="caption" color="text.secondary">
                            {formatDate(invoice.date)}
                          </Typography>
                        )}
                      </TableCell>
                      {!isMobile && <TableCell>{formatDate(invoice.date)}</TableCell>}
                      <TableCell>
                        <Typography variant="body2" fontWeight={500}>
                          {formatCurrency(invoice.amount)}
                        </Typography>
                        {invoice.paid > 0 && invoice.paid < invoice.amount && (
                          <Typography variant="caption" color="text.secondary">
                            Paid: {formatCurrency(invoice.paid)}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>{getStatusChip(invoice.status)}</TableCell>
                      <TableCell align="center">
                        <IconButton 
                          size="small" 
                          title="View Invoice"
                          onClick={() => openDetailView('invoice', invoice)}
                        >
                          <OpenInNewIcon fontSize="small" />
                        </IconButton>
                        <IconButton size="small" title="Download PDF">
                          <DownloadIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </TabPanel>

        {/* Payments Tab */}
        <TabPanel value={tabValue} index={2}>
          {payments.length === 0 ? (
            <Alert severity="info">No payments recorded</Alert>
          ) : (
            <TableContainer component={Paper} variant="outlined" sx={{ overflowX: 'auto' }}>
              <Table size={isMobile ? 'small' : 'medium'} sx={{ minWidth: isMobile ? 300 : 500 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell>Amount</TableCell>
                    {!isMobile && <TableCell>Method</TableCell>}
                    <TableCell>Reference</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {payments.map((payment) => (
                    <TableRow key={payment.id} hover>
                      <TableCell>
                        <Typography variant="body2">
                          {formatDate(payment.date)}
                        </Typography>
                        {isMobile && (
                          <Typography variant="caption" color="text.secondary">
                            {payment.method}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={500}>
                          {formatCurrency(payment.amount)}
                        </Typography>
                      </TableCell>
                      {!isMobile && <TableCell>{payment.method}</TableCell>}
                      <TableCell>
                        <Typography variant="body2">
                          {payment.reference}
                        </Typography>
                        {payment.invoice && (
                          <Typography variant="caption" color="text.secondary">
                            Invoice: {payment.invoice}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>{getStatusChip(payment.status)}</TableCell>
                      <TableCell align="center">
                        <IconButton 
                          size="small" 
                          title="View Payment Details"
                          onClick={() => openDetailView('payment', payment)}
                        >
                          <OpenInNewIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </TabPanel>

      </CardContent>
    </Card>
    
    {/* Detail View Dialog */}
    <BillingDetailView
      open={detailView.open}
      onClose={closeDetailView}
      type={detailView.type}
      data={detailView.data}
    />
  </>
  );
};